import { siteConfig } from "@acme/config";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  LegalPage,
  LegalSection,
  Requisite,
} from "~/components/legal/legal-page";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getLegalPageSchemas,
} from "~/lib/structured-data";
import { getBaseUrl } from "~/lib/utils";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/legal")({
  component: RouteComponent,
  head: () => {
    const seoData = seo({
      description: m.meta_legal_description(),
      keywords:
        "Lindevo Legal Information, Rekviziti, Nauris Linde, Saimnieciskas darbibas veicejs, Imprint",
      noindex: true,
      path: "/legal",
      title: m.meta_legal_title(),
    });
    const structuredData = generateStructuredDataGraph(getLegalPageSchemas());
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

function getSections() {
  return [
    { id: "requisites", title: m.legal_requisites_title() },
    { id: "data-protection", title: m.legal_data_title() },
  ];
}

function RouteComponent() {
  // The statutory "place of business" is this website, so it has to name the
  // origin actually being served — a preview or localhost deployment must not
  // claim to be lindevo.com.
  const siteHost = new URL(getBaseUrl()).host;

  return (
    <LegalPage
      label={m.legal_label()}
      lede={m.legal_intro()}
      sections={getSections()}
      title={m.legal_heading()}
    >
      {/* The correction itself, first and on its own ground: whoever arrives
          here is asking what Lindevo legally is. */}
      <p className="legal-note">{m.legal_disclaimer()}</p>

      <LegalSection id="requisites" title={m.legal_requisites_title()}>
        <dl className="facts">
          <Requisite term={m.legal_term_name()}>
            {siteConfig.legal.name}
          </Requisite>

          <Requisite term={m.legal_term_status()}>
            <time dateTime={siteConfig.legal.registeredSince}>
              {m.legal_value_status()}
            </time>
          </Requisite>

          <Requisite note={m.legal_tax_id_note()} term={m.legal_term_tax_id()}>
            <span className="legal-code">
              {siteConfig.legal.taxIdSecondPart}
            </span>
          </Requisite>

          <Requisite note={m.legal_place_note()} term={m.legal_term_place()}>
            <a href={getBaseUrl()}>{siteHost}</a>
          </Requisite>

          <Requisite
            note={m.legal_address_note()}
            term={m.legal_term_address()}
          >
            {`${siteConfig.legal.addressPublic}, ${m.legal_value_country()}`}
          </Requisite>

          <Requisite term={m.legal_term_email()}>
            <a href={`mailto:${siteConfig.contacts.mail}`}>
              {siteConfig.contacts.mail}
            </a>
          </Requisite>

          <Requisite term={m.legal_term_phone()}>
            <a href={`tel:${siteConfig.contacts.phone}`}>
              {siteConfig.contacts.phoneDisplay}
            </a>
          </Requisite>

          <Requisite term={m.legal_term_vat()}>
            {siteConfig.legal.vatNumber ? (
              <span className="legal-code">{siteConfig.legal.vatNumber}</span>
            ) : (
              m.legal_value_vat_not_registered()
            )}
          </Requisite>

          <Requisite term={m.legal_term_activity()}>
            {m.legal_value_activity({ code: siteConfig.legal.nace })}
          </Requisite>

          <Requisite term={m.legal_term_supervisor()}>
            <a
              href="https://www.ptac.gov.lv"
              rel="noopener noreferrer"
              target="_blank"
            >
              {m.legal_value_supervisor()} <span aria-hidden="true">↗</span>
              <span className="sr-only">({m.legal_opens_new_tab()})</span>
            </a>
          </Requisite>
        </dl>
      </LegalSection>

      <LegalSection id="data-protection" title={m.legal_data_title()}>
        <p>
          {m.legal_data_body()}{" "}
          <Link to="/privacy-policy">{m.footer_privacy_policy()}</Link>.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
